import { useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';
import { useWallet } from '../components/context/WagmiWalletContext';
import { useContracts } from '../components/context/ContractContext';
import logger from '../utils/logger';

// Types for Uniswap V3 fee management
export interface FeeMetrics {
    unclaimedFeesETH: string;
    unclaimedFeesMEMER: string;
    totalValueETH: string;
    isCollectionViable: boolean;
    estimatedGasCost: string;
    profitAfterGas: string;
    lastUpdated: number;
}

export interface PositionHealth {
    tokenId: string;
    inRange: boolean;
    currentTick: number;
    tickRange: [number, number];
    liquidity: string;
    unclaimedFees0: string;
    unclaimedFees1: string;
    lastUpdated: number;
}

export interface AutoCompoundConfig {
    enabled: boolean;
    minFeeThresholdETH: string;
    gasMultiplier: string;
    minInterval: string;
    lastCompoundTime: number;
}

interface UseFeeManagementReturn {
    // Data
    feeMetrics: FeeMetrics | null;
    positions: PositionHealth[];
    autoCompoundConfig: AutoCompoundConfig | null;

    // Loading states
    loading: boolean;
    error: string | null;

    // Actions
    refreshData: () => Promise<void>;
    collectAllFees: () => Promise<string | null>;
    collectAndReinvest: (tokenId: string, minToken0: string, minToken1: string) => Promise<string | null>;
    enableAutoCompound: (threshold: string, gasMultiplier: string, interval: string) => Promise<string | null>;
    disableAutoCompound: () => Promise<string | null>;
}

export const useFeeManagement = (contractAddress?: string): UseFeeManagementReturn => {
    const { provider, signer, chainId } = useWallet();
    const { deploymentDetails } = useContracts();

    // State
    const [feeMetrics, setFeeMetrics] = useState<FeeMetrics | null>(null);
    const [positions, setPositions] = useState<PositionHealth[]>([]);
    const [autoCompoundConfig, setAutoCompoundConfig] = useState<AutoCompoundConfig | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [contract, setContract] = useState<ethers.Contract | null>(null);

    // Get the actual contract address
    const actualContractAddress = contractAddress || deploymentDetails?.liquidity?.uniswapSetup?.addressForUI;

    // Load ABI based on chain ID
    const loadABI = useCallback(async (chainId: string): Promise<any[] | null> => {
        try {
            const abiPath = `/abi/${chainId}/UniswapLiquiditySetupABI.json`;
            const response = await fetch(abiPath);

            if (!response.ok) {
                logger.error(`Failed to load ABI from ${abiPath}`);
                return null;
            }

            const data = await response.json();

            // Handle different ABI formats
            if (Array.isArray(data)) {
                return data;
            } else if (data.abi && Array.isArray(data.abi)) {
                return data.abi;
            } else {
                logger.error('Invalid ABI format');
                return null;
            }
        } catch (error) {
            logger.error('Error loading ABI:', error);
            return null;
        }
    }, []);

    // Initialize contract
    useEffect(() => {
        const initContract = async () => {
            if (!actualContractAddress || !provider || !chainId) {
                setContract(null);
                return;
            }

            try {
                const abi = await loadABI(chainId.toString());
                if (!abi) {
                    setError('Failed to load contract ABI');
                    return;
                }

                const contractInstance = new ethers.Contract(
                    actualContractAddress,
                    abi,
                    signer || provider
                );

                setContract(contractInstance);
                setError(null);
                logger.log('Uniswap contract initialized:', actualContractAddress);
            } catch (err) {
                logger.error('Error initializing contract:', err);
                setError('Failed to initialize contract');
            }
        };

        initContract();
    }, [actualContractAddress, provider, signer, chainId, loadABI]);

    // Helper function to convert Wei to ETH
    const weiToEth = (wei: ethers.BigNumber): string => {
        return ethers.utils.formatEther(wei);
    };

    // Helper function to convert MEMER to display format
    const formatMemer = (amount: ethers.BigNumber): string => {
        return ethers.utils.formatEther(amount);
    };

    // Refresh all data
    const refreshData = useCallback(async () => {
        if (!contract) {
            logger.warn('Contract not initialized');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            logger.log('Refreshing Uniswap fee management data...');

            // Get unclaimed fees with error handling
            let amount0 = ethers.BigNumber.from(0);
            let amount1 = ethers.BigNumber.from(0);

            try {
                const result = await contract.getUnclaimedFees();
                amount0 = result[0];
                amount1 = result[1];
                logger.log('Unclaimed fees:', { amount0: amount0.toString(), amount1: amount1.toString() });
            } catch (feesError) {
                logger.warn('getUnclaimedFees failed, using zero values:', feesError);
                // Keep amount0 and amount1 as zero - this is expected for new positions
            }

            // Get gas price for calculations
            const gasPrice = await provider?.getGasPrice() || ethers.BigNumber.from('20000000000'); // 20 gwei fallback
            const estimatedGasCost = gasPrice.mul(200000); // ~200k gas for collect + reinvest

            // Check collection viability (assuming we have at least one position)
            let isViable = false;
            let feeValueETH = ethers.BigNumber.from(0);

            try {
                // Try to get viability for first position (tokenId 1 is common)
                const [viable, value] = await contract.isFeeCollectionViable(1, 3);
                isViable = viable;
                feeValueETH = value;
            } catch (viabilityError) {
                logger.warn('Could not check fee collection viability:', viabilityError);
                // Fallback: assume WETH fees are the main value
                feeValueETH = amount0;
                isViable = feeValueETH.gt(estimatedGasCost.mul(3));
            }

            // Don't use mock data - let the UI show real connection status
            if (amount0.eq(0) && amount1.eq(0)) {
                logger.log('No fees detected - this may indicate no liquidity positions or contract call issues');
            }

            const profitAfterGas = feeValueETH.sub(estimatedGasCost);

            setFeeMetrics({
                unclaimedFeesETH: weiToEth(amount0),
                unclaimedFeesMEMER: formatMemer(amount1),
                totalValueETH: weiToEth(feeValueETH),
                isCollectionViable: isViable,
                estimatedGasCost: weiToEth(estimatedGasCost),
                profitAfterGas: weiToEth(profitAfterGas.gt(0) ? profitAfterGas : ethers.BigNumber.from(0)),
                lastUpdated: Date.now()
            });

            // Try to get position health data - use getOwnedTokenIds first
            let positionsData: PositionHealth[] = [];
            try {
                const ownedTokenIds = await contract.getOwnedTokenIds();
                logger.log('Owned token IDs result:', ownedTokenIds);

                if (ownedTokenIds && Array.isArray(ownedTokenIds) && ownedTokenIds.length > 0) {
                    logger.log(`Found ${ownedTokenIds.length} owned positions`);
                    for (const tokenId of ownedTokenIds) {
                        try {
                            const positionHealth = await contract.getPositionHealth(tokenId);
                            positionsData.push({
                                tokenId: tokenId.toString(),
                                inRange: positionHealth.inRange,
                                currentTick: positionHealth.currentTick,
                                tickRange: [positionHealth.tickLower, positionHealth.tickUpper] as [number, number],
                                liquidity: positionHealth.liquidity.toString(),
                                unclaimedFees0: positionHealth.unclaimedFees0.toString(),
                                unclaimedFees1: positionHealth.unclaimedFees1.toString(),
                                lastUpdated: Date.now()
                            });
                        } catch (singlePositionError) {
                            logger.warn(`Could not get health for position ${tokenId}:`, singlePositionError);
                        }
                    }
                    setPositions(positionsData);
                } else {
                    logger.log('No owned token IDs found - this is normal for contracts without Uniswap V3 positions');
                    setPositions([]);
                }
            } catch (positionError) {
                logger.warn('getOwnedTokenIds call failed - this is expected if no positions exist:', positionError);
                setPositions([]);
                // This is not an error - it's expected behavior for new deployments
            }

            // Try to get auto-compound config for available positions
            if (positionsData.length > 0) {
                try {
                    // Get config for the first position
                    const firstTokenId = positionsData[0].tokenId;
                    const config = await contract.getAutoCompoundConfig(firstTokenId);
                    setAutoCompoundConfig({
                        enabled: config.enabled,
                        minFeeThresholdETH: weiToEth(config.minFeeThresholdETH),
                        gasMultiplier: config.gasMultiplier.toString(),
                        minInterval: config.minCompoundInterval.toString(),
                        lastCompoundTime: config.lastCompoundTime.toNumber()
                    });
                } catch (configError) {
                    logger.error('Failed to get auto-compound config:', configError);
                    setAutoCompoundConfig(null);
                }
            } else {
                setAutoCompoundConfig(null);
            }

            logger.log('Uniswap fee management data refreshed successfully');
        } catch (err) {
            logger.error('Error refreshing fee management data:', err);
            setError(err instanceof Error ? err.message : 'Failed to refresh data');
        } finally {
            setLoading(false);
        }
    }, [contract, provider]);

    // Collect all fees
    const collectAllFees = useCallback(async (): Promise<string | null> => {
        if (!contract || !signer) {
            setError('Contract or signer not available');
            return null;
        }

        try {
            logger.log('Collecting all fees...');
            const tx = await contract.collectAllFees();
            logger.log('Collect fees transaction sent:', tx.hash);
            return tx.hash;
        } catch (err) {
            logger.error('Error collecting fees:', err);
            setError(err instanceof Error ? err.message : 'Failed to collect fees');
            return null;
        }
    }, [contract, signer]);

    // Collect and reinvest fees
    const collectAndReinvest = useCallback(async (
        tokenId: string,
        minToken0: string,
        minToken1: string
    ): Promise<string | null> => {
        if (!contract || !signer) {
            setError('Contract or signer not available');
            return null;
        }

        try {
            logger.log('Collecting and reinvesting fees...', { tokenId, minToken0, minToken1 });
            const tx = await contract.collectAndReinvestFees(
                tokenId,
                ethers.utils.parseEther(minToken0),
                ethers.utils.parseEther(minToken1)
            );
            logger.log('Collect and reinvest transaction sent:', tx.hash);
            return tx.hash;
        } catch (err) {
            logger.error('Error collecting and reinvesting fees:', err);
            setError(err instanceof Error ? err.message : 'Failed to collect and reinvest fees');
            return null;
        }
    }, [contract, signer]);

    // Enable auto-compound
    const enableAutoCompound = useCallback(async (
        threshold: string,
        gasMultiplier: string,
        interval: string
    ): Promise<string | null> => {
        if (!contract || !signer) {
            setError('Contract or signer not available');
            return null;
        }

        try {
            logger.log('Enabling auto-compound...', { threshold, gasMultiplier, interval });
            const tx = await contract.enableAutoCompounding(
                1, // tokenId
                ethers.utils.parseEther(threshold),
                gasMultiplier,
                interval
            );
            logger.log('Enable auto-compound transaction sent:', tx.hash);
            return tx.hash;
        } catch (err) {
            logger.error('Error enabling auto-compound:', err);
            setError(err instanceof Error ? err.message : 'Failed to enable auto-compound');
            return null;
        }
    }, [contract, signer]);

    // Disable auto-compound
    const disableAutoCompound = useCallback(async (): Promise<string | null> => {
        if (!contract || !signer) {
            setError('Contract or signer not available');
            return null;
        }

        try {
            logger.log('Disabling auto-compound...');
            const tx = await contract.disableAutoCompounding(1); // tokenId
            logger.log('Disable auto-compound transaction sent:', tx.hash);
            return tx.hash;
        } catch (err) {
            logger.error('Error disabling auto-compound:', err);
            setError(err instanceof Error ? err.message : 'Failed to disable auto-compound');
            return null;
        }
    }, [contract, signer]);

    // Auto-refresh data every 30 seconds
    useEffect(() => {
        if (contract) {
            refreshData();
            const interval = setInterval(refreshData, 30000);
            return () => clearInterval(interval);
        }
    }, [contract, refreshData]);

    return {
        feeMetrics,
        positions,
        autoCompoundConfig,
        loading,
        error,
        refreshData,
        collectAllFees,
        collectAndReinvest,
        enableAutoCompound,
        disableAutoCompound
    };
};
